import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import {
  SmartSheet,
  SmartTableDetail,
  unwrapSmartTableCursorCollectionPayload,
  unwrapSmartTableEntityPayload,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface SmartTableBootstrap {
  table: SmartTableDetail;
  sheets: SmartSheet[];
}

const fetchAllSmartTableSheets = async ({
  wid,
  tableId,
  headers,
}: {
  wid: number;
  tableId: string;
  headers: HeadersInit;
}): Promise<SmartSheet[]> => {
  const sheets: SmartSheet[] = [];
  let cursor: string | null = null;

  do {
    const sheetsUrl = new URL(ENDPOINTS.smart_table_sheets(wid, tableId));
    sheetsUrl.searchParams.set("limit", "100");
    if (cursor) {
      sheetsUrl.searchParams.set("cursor", cursor);
    }

    const response = await fetch(sheetsUrl.toString(), { headers });
    const payload = await handleResponse(response);
    const page = unwrapSmartTableCursorCollectionPayload<SmartSheet>(payload, "sheets");
    sheets.push(...page.items);
    cursor = page.next_cursor;
  } while (cursor);

  return sheets;
};

export const fetchSmartTableBootstrap = async ({
  wid,
  tableId,
  userToken,
}: {
  wid: number;
  tableId: string;
  userToken: string;
}): Promise<SmartTableBootstrap> => {
  const headers = jsonAuthHeaders(userToken);

  const [tablePayload, sheets] = await Promise.all([
    fetch(ENDPOINTS.smart_table(wid, tableId), { headers }).then(handleResponse),
    fetchAllSmartTableSheets({ wid, tableId, headers }),
  ]);

  const table = unwrapSmartTableEntityPayload<SmartTableDetail>(tablePayload, "smart_table");

  return {
    table,
    sheets,
  };
};

export const useGetSmartTableBootstrap = (tableId?: string, enabled = true) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  return useQuery<SmartTableBootstrap>({
    queryKey: ["smart-tables", wid, tableId, "bootstrap"],
    queryFn: async () =>
      fetchSmartTableBootstrap({
        wid,
        tableId: tableId!,
        userToken,
      }),
    enabled: enabled && !!workspaceId && !!tableId,
  });
};
